import { useAppDispatch, useAppSelector } from 'app/hooks';
import LoadingSpinner from 'components/common/loading-spinner';
import TextKey from 'components/common/text-key';
import ConfidenceGateBanner from 'components/equipment-page/confidence-gate-banner';
import EquipmentComparisonView from 'components/equipment-page/equipment-comparison-view';
import EquipmentSystemsErrorNote from 'components/equipment-page/equipment-systems-error-note';
import LocationRegulationsBanner from 'components/equipment-page/location-regulations-banner';
import SizingBandsBanner from 'components/equipment-page/sizing-bands-banner';
import InternalNote from 'components/layout/internal-note';
import { useFooterConfig } from 'contexts/footer-context';
import {
  chooseSystemsEmptyMessages,
  proceedErrorMessages,
  tabGatingMessages
} from 'data/constants';
import { retryPtacSystemsFetch } from 'features/ptac-selection';
import { bumpValidSystemsRetry } from 'features/systems';
import { hasFailedValidSystemsSource, setActiveTab } from 'features/ui';
import useCustomSystemRefreshState from 'hooks/use-custom-system-refresh-state';
import useExpectedFullFetchConfigIds from 'hooks/use-expected-full-fetch-config-ids';
import useHybridLeftoverSpaceIds from 'hooks/use-hybrid-leftover-space-ids';
import useIsCompatibilityPathway from 'hooks/use-is-compatibility-pathway';
import useIsPtacPathway from 'hooks/use-is-ptac-pathway';
import { useHasQuotableSystems } from 'hooks/use-quoted-unique-ids';
import useSystemDesignBypassMode from 'hooks/use-system-design-bypass-mode';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { selectActivePtacConfiguration } from 'utils/active-ptac-configuration';
import {
  CHOOSE_SYSTEMS_PAGE_TITLE,
  CHOOSE_SYSTEMS_PAGE_TITLE_KEY
} from 'utils/choose-systems-page-title';
import {
  isConfigurationComplete,
  isPtacDesignReady
} from 'utils/configuration-eligibility';
import { isCustomSystemRefreshPending } from 'utils/custom-system-refresh';
import {
  selectDuctedPairLinkMode,
  selectIsDuctedDesignPending
} from 'utils/ducted-design';
import { ductedDesignPendingChooseSystemsMessage } from 'utils/ducted-design-copy';
import { getChooseSystemsEligibleDuctedSystems } from 'utils/existing-equipment';
import { isSystemDesignLocked } from 'utils/system-design-bypass';
import { warningModal } from 'utils/ui';
import { areExpectedFullFetchesSettled } from 'utils/valid-systems-settlement';

export default function ChooseSystemsPage() {
  const dispatch = useAppDispatch();
  const configurations = useAppSelector(
    (state) => state.configurations.configurations
  );
  const systemCombinationsFull = useAppSelector(
    (state) => state.systems.systemCombinationsFull
  );
  const customSystemRefreshState = useCustomSystemRefreshState();
  const {
    pageProgress,
    fetchingFullConfigIds,
    failedValidSystemsConfigSources
  } = useAppSelector((state) => state.ui);
  const hasQuotableSystems = useHasQuotableSystems();
  const isDucted = useIsCompatibilityPathway();
  const isPtac = useIsPtacPathway();
  const bypassMode = useSystemDesignBypassMode();
  const systemDesignLocked = isSystemDesignLocked(bypassMode);
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  const wantCompositionPicksBySystem = useAppSelector(
    (state) => state.structure.wantCompositionPicksBySystem
  );
  const recommendationSelectionsLoaded = useAppSelector(
    (state) => state.structure.recommendationSelectionsLoaded
  );

  // Ducted-path mount gate (AHA-6681 CS-2): eligibility-based, not
  // results-based — the ducted fetch hook lives inside the view, so gating
  // on its results would never let it mount to fetch them. Eligibility
  // includes zero-component systems with a completed strip (virtual-tile
  // haves) via the shared selector.
  const hasEligibleDuctedSystems = useMemo(
    () =>
      isDucted &&
      getChooseSystemsEligibleDuctedSystems(
        existingSystems,
        recommendationSelectionsBySystem,
        wantCompositionPicksBySystem
      ).length > 0,
    [
      isDucted,
      existingSystems,
      recommendationSelectionsBySystem,
      wantCompositionPicksBySystem
    ]
  );
  const ductedDesignPending = useAppSelector(selectIsDuctedDesignPending);
  const ductedPairLinkMode = useAppSelector(selectDuctedPairLinkMode);
  const ductedSystemsReady = hasEligibleDuctedSystems && !ductedDesignPending;

  // PTAC mount gate: the systems are composed from a complete System Design
  // (every unit placed with its room) — the units are what the fetch sizes.
  const ptacConfigurationId = useAppSelector(
    (state) => selectActivePtacConfiguration(state)?.uniqueId ?? null
  );
  const hasCompletePtacDesign = useAppSelector((state) =>
    isPtacDesignReady(selectActivePtacConfiguration(state))
  );
  const ptacSystemsStatus = useAppSelector(
    (state) => state.ptacSelection.systems.status
  );
  const hasPtacOptions = useAppSelector(
    (state) => state.ptacSelection.systems.options.length > 0
  );
  const ptacSystemsSettled =
    !hasCompletePtacDesign ||
    ptacSystemsStatus === 'loaded' ||
    ptacSystemsStatus === 'error';
  const ptacSystemsFailed =
    hasCompletePtacDesign && ptacSystemsStatus === 'error';
  const ptacSwapRefreshPending =
    ptacConfigurationId !== null &&
    isCustomSystemRefreshPending(customSystemRefreshState, ptacConfigurationId);
  const ptacSystemsRefreshing =
    hasCompletePtacDesign &&
    ptacSystemsStatus === 'loading' &&
    hasPtacOptions &&
    !ptacSwapRefreshPending;

  const leftoverSpaceIds = useHybridLeftoverSpaceIds();
  const willFetchConfigIds = useExpectedFullFetchConfigIds();

  const allExpectedSettled = useMemo(
    () =>
      areExpectedFullFetchesSettled({
        expectedConfigIds: willFetchConfigIds,
        systemCombinationsFull,
        failedValidSystemsConfigSources
      }),
    [
      willFetchConfigIds,
      systemCombinationsFull,
      failedValidSystemsConfigSources
    ]
  );

  const fetchingCompleteConfigIds = useMemo(
    () =>
      fetchingFullConfigIds.filter((id) =>
        isConfigurationComplete(
          configurations.find((config) => config.uniqueId === id),
          leftoverSpaceIds
        )
      ),
    [fetchingFullConfigIds, configurations, leftoverSpaceIds]
  );

  // On the ducted pathway the gate reads the recommendation-selections
  // mirror, which hydrates async from loadStructure — with zero
  // configurations the settlement check is vacuously true on first render,
  // so without this guard a virtual-only structure would flash the empty
  // note before hydration lands. The 15s timeout below stays the cap.
  const dataReady =
    allExpectedSettled &&
    ptacSystemsSettled &&
    fetchingCompleteConfigIds.length === 0 &&
    (!isDucted || recommendationSelectionsLoaded);

  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  useEffect(() => {
    if (hasInitiallyLoaded) return;
    if (dataReady) setHasInitiallyLoaded(true);
  }, [hasInitiallyLoaded, dataReady]);

  useEffect(() => {
    if (hasInitiallyLoaded) return undefined;
    const timeout = setTimeout(() => setHasInitiallyLoaded(true), 15000);
    return () => clearTimeout(timeout);
  }, [hasInitiallyLoaded]);

  const isRefreshing = fetchingCompleteConfigIds.length > 0;

  const isBlockingRefresh = useMemo(
    () =>
      fetchingCompleteConfigIds.some((id) => {
        const config = configurations.find((c) => c.uniqueId === id);
        return (
          config?.condensers.some(
            (c) => c.position !== undefined && !c.validSystemsFetched
          ) ?? false
        );
      }),
    [fetchingCompleteConfigIds, configurations]
  );

  const failedFullConfigIds = useMemo(
    () =>
      willFetchConfigIds.filter(
        (id) =>
          hasFailedValidSystemsSource(
            failedValidSystemsConfigSources,
            id,
            'full'
          ) && !Object.prototype.hasOwnProperty.call(systemCombinationsFull, id)
      ),
    [
      willFetchConfigIds,
      failedValidSystemsConfigSources,
      systemCombinationsFull
    ]
  );

  const hasFailures = failedFullConfigIds.length > 0 || ptacSystemsFailed;
  const failedCount = failedFullConfigIds.length + (ptacSystemsFailed ? 1 : 0);

  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!retrying) return undefined;
    if (isRefreshing || ptacSystemsStatus === 'loading') {
      setRetrying(false);
      return undefined;
    }
    const timeout = setTimeout(() => setRetrying(false), 5000);
    return () => clearTimeout(timeout);
  }, [retrying, isRefreshing, ptacSystemsStatus]);

  const handleRetry = useCallback(() => {
    if (failedFullConfigIds.length === 0 && !ptacSystemsFailed) return;
    setRetrying(true);
    failedFullConfigIds.forEach((id) => {
      dispatch(bumpValidSystemsRetry({ configId: id }));
    });
    if (ptacSystemsFailed) {
      dispatch(retryPtacSystemsFetch());
    }
  }, [dispatch, failedFullConfigIds, ptacSystemsFailed]);

  const showOverlaySpinner = isBlockingRefresh || retrying;
  const hasNonSwapRefresh = fetchingCompleteConfigIds.some(
    (id) => !isCustomSystemRefreshPending(customSystemRefreshState, id)
  );
  const showBackgroundRefresh =
    (hasNonSwapRefresh || ptacSystemsRefreshing) && !showOverlaySpinner;

  const handleBack = useCallback(() => {
    // A hidden System Design was skipped on the way in (ducted-only, or
    // hybrid with no mini-split rooms) — Back returns to Layout & Sizing,
    // not the page Next just skipped.
    dispatch(setActiveTab(systemDesignLocked ? 'structure' : 'configurations'));
  }, [dispatch, systemDesignLocked]);

  const handleNext = useCallback(() => {
    if (pageProgress.chooseSystems === 100 && hasQuotableSystems) {
      dispatch(setActiveTab('accessories'));
    } else if (!hasQuotableSystems) {
      warningModal(tabGatingMessages.noQuotableSystems);
    } else {
      warningModal(proceedErrorMessages.chooseSystems);
    }
  }, [dispatch, pageProgress.chooseSystems, hasQuotableSystems]);

  const isNextDisabled =
    !hasQuotableSystems || pageProgress.chooseSystems !== 100;

  // Mirrors handleNext's precedence: the quotable gate is checked first,
  // so the tooltip matches the modal a click would show.
  // eslint-disable-next-line no-nested-ternary
  const nextDisabledReason = !hasQuotableSystems
    ? tabGatingMessages.noQuotableSystems
    : pageProgress.chooseSystems !== 100
    ? proceedErrorMessages.chooseSystems
    : undefined;

  useFooterConfig(
    useMemo(
      () => ({
        onBack: handleBack,
        onNext: handleNext,
        backText: 'Back',
        nextText: 'Next Section',
        nextDisabled: isNextDisabled,
        nextDisabledReason,
        nextVariant: 'green' as const,
        showBackArrow: true
      }),
      [handleBack, handleNext, isNextDisabled, nextDisabledReason]
    )
  );

  let content: JSX.Element;
  if (!hasInitiallyLoaded) {
    content = (
      <div className='flex min-h-[600px] w-full flex-1 items-center justify-center'>
        <LoadingSpinner />
      </div>
    );
  } else if (
    hasCompletePtacDesign ||
    ductedSystemsReady ||
    willFetchConfigIds.length > 0
  ) {
    content = (
      <div className='relative flex min-h-0 flex-1 flex-col gap-5'>
        {hasFailures && (
          <EquipmentSystemsErrorNote
            failedCount={failedCount}
            isRetrying={retrying}
            onRetry={handleRetry}
          />
        )}
        {/* Location-regulations banner: the server drops every system the
            cart would flag for the project ZIP (Low NOx / Ultra-Low NOx
            furnaces, ...); informational, no opt-out — the rules are law.
            Renders nothing when they excluded nothing. */}
        {ductedSystemsReady && <LocationRegulationsBanner />}
        {/* Sizing-bands banner (AHA-6796 T3): server-side band constraint
            surfaced with a one-click relax; renders nothing when bands
            never excluded anything. */}
        {ductedSystemsReady && <SizingBandsBanner />}
        {ductedSystemsReady && <ConfidenceGateBanner />}
        <EquipmentComparisonView />
        {showOverlaySpinner && (
          <div className='absolute inset-0 z-30 flex items-center justify-center bg-white/60'>
            <LoadingSpinner />
          </div>
        )}
        {showBackgroundRefresh && (
          <div className='absolute right-4 top-4 z-10 flex items-center gap-2 rounded-full bg-white px-3 py-1.5 shadow-md ring-1 ring-black/5'>
            <LoadingSpinner className='h-4 w-4 animate-spin text-gray-500' />
            <span className='text-xs font-medium text-gray-600'>
              Updating systems...
            </span>
          </div>
        )}
      </div>
    );
  } else if (isRefreshing || retrying) {
    content = (
      <div className='flex min-h-[600px] w-full flex-1 items-center justify-center'>
        <LoadingSpinner />
      </div>
    );
  } else if (hasFailures) {
    content = (
      <EquipmentSystemsErrorNote
        failedCount={failedCount}
        isRetrying={retrying}
        onRetry={handleRetry}
      />
    );
  } else if (isPtac) {
    // PTAC with an incomplete design: units come from System Design.
    content = (
      <InternalNote>{chooseSystemsEmptyMessages.noPtacDesign}</InternalNote>
    );
  } else if (isDucted && hasEligibleDuctedSystems && ductedDesignPending) {
    content = (
      <InternalNote>
        {ductedDesignPendingChooseSystemsMessage(ductedPairLinkMode)}
      </InternalNote>
    );
  } else if (isDucted) {
    // Ducted with no eligible system (no components, strip not completed):
    // the complete-a-configuration message would steer to the wrong page —
    // recommendations come from Existing Equipment on this pathway.
    content = (
      <InternalNote>
        {chooseSystemsEmptyMessages.noDuctedRecommendations}
      </InternalNote>
    );
  } else if (bypassMode === 'hybrid-whole-house') {
    // System Design is hidden here and the whole-house configuration is
    // generated from Layout & Sizing — steering at System Design would be
    // wrong.
    content = (
      <InternalNote>
        {chooseSystemsEmptyMessages.hybridWholeHousePending}
      </InternalNote>
    );
  } else {
    content = (
      <InternalNote>
        {chooseSystemsEmptyMessages.noCompleteConfiguration}
      </InternalNote>
    );
  }

  return (
    <div className='mx-auto flex h-full max-w-[1700px] flex-col'>
      <h2 className='w-full shrink-0 pb-8 pt-5 text-center text-4xl font-semibold text-gray-900'>
        <TextKey
          code={CHOOSE_SYSTEMS_PAGE_TITLE_KEY}
          fallback={CHOOSE_SYSTEMS_PAGE_TITLE}
        />
      </h2>
      <div className='flex min-h-0 grow flex-col gap-5'>{content}</div>
    </div>
  );
}
